import { generateClient } from 'aws-amplify/api';
import { useCallback, useRef, useState } from 'react';
import logger from '../utils/logger';
import { AGENT_PHASES, AGENT_EVENT_TYPES } from '../constants';
import { CREATE_ITINERARY, GET_ITINERARY, SEND_CHAT, ON_AGENT_UPDATE, ON_ITINERARY_UPDATE } from '../graphql';
import type { AgentEvent, Itinerary } from '../types';

type Phase = 'idle' | 'running' | 'complete' | 'error';

interface RawAgentEvent {
  sessionId: string;
  eventType: string;
  message: string;
  toolName?: string;
  modelName?: string;
  data?: string;
  timestamp: string;
}

function toAgentEvent(raw: RawAgentEvent): AgentEvent {
  return {
    sessionId: raw.sessionId,
    eventType: raw.eventType,
    message: raw.message,
    toolName: raw.toolName ?? undefined,
    modelName: raw.modelName ?? undefined,
    data: raw.data,
    timestamp: raw.timestamp,
  };
}

export function useAgentUpdates() {
  const clientRef = useRef(generateClient());
  const client = clientRef.current;
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [phase, setPhase] = useState<Phase>('idle');
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'assistant'; text: string }[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const subRef = useRef<{ unsubscribe: () => void } | null>(null);
  const itSubRef = useRef<{ unsubscribe: () => void } | null>(null);
  const chatSubRef = useRef<{ unsubscribe: () => void } | null>(null);
  const chatBufferRef = useRef('');

  const fetchItinerary = useCallback(async (sessionId: string) => {
    try {
      const res: any = await client.graphql({
        query: GET_ITINERARY,
        variables: { sessionId },
      });
      const result = res.data.getItinerary;
      if (result?.itinerary) {
        setItinerary(result);
        setPhase('complete');
      } else {
        logger.error('No itinerary in response', result);
        setError('Itinerary was empty');
        setPhase('error');
      }
    } catch (err) {
      logger.error('Failed to fetch itinerary', err);
      setError('Failed to fetch itinerary');
      setPhase('error');
    }
  }, [client]);

  const generateItinerary = useCallback(async (input: Record<string, unknown>) => {
    setEvents([]);
    setPhase('running');
    setItinerary(null);
    setError(null);

    const sessionId = input.sessionId as string;
    subRef.current?.unsubscribe();

    subRef.current = (client.graphql({
      query: ON_AGENT_UPDATE,
      variables: { sessionId },
    }) as any).subscribe({
      next: (msg: any) => {
        try {
          const raw: RawAgentEvent = msg?.data?.onAgentUpdate;
          if (!raw) return;

          logger.debug('agent event received', { type: raw.eventType, tool: raw.toolName, message: raw.message });

          const evt = toAgentEvent(raw);

          if (evt.eventType === AGENT_EVENT_TYPES.THINKING) {
            if (!evt.message.trim()) return;
            setEvents(prev => {
              const last = prev[prev.length - 1];
              if (last?.eventType === AGENT_EVENT_TYPES.THINKING) {
                return [...prev.slice(0, -1), { ...last, message: last.message + evt.message }];
              }
              return [...prev, evt];
            });
            return;
          }

          setEvents(prev => [...prev, evt]);

          if (evt.eventType === AGENT_EVENT_TYPES.AGENT_COMPLETE) {
            fetchItinerary(sessionId);
          }
          if (evt.eventType === AGENT_EVENT_TYPES.AGENT_ERROR) {
            setError(evt.message);
            setPhase('error');
          }
        } catch (e) {
          logger.error('Subscription handler error', e);
        }
      },
      error: (err: unknown) => {
        logger.error('Subscription error', err);
        setError('Lost connection to agent updates');
        setPhase(AGENT_PHASES.ERROR);
      },
    });

    // Subscribe to itinerary updates (postcardUrl, pdfUrl, status changes)
    itSubRef.current?.unsubscribe();
    itSubRef.current = (client.graphql({
      query: ON_ITINERARY_UPDATE,
      variables: { sessionId },
    }) as any).subscribe({
      next: (msg: any) => {
        try {
          const update = msg?.data?.onItineraryUpdate;
          if (!update) return;
          logger.info('itinerary update received', { sessionId: update.sessionId, status: update.status, postcardUrl: update.postcardUrl });
          setItinerary(prev => prev ? { ...prev, ...update } : update);
        } catch (e) {
          logger.error('Itinerary subscription handler error', e);
        }
      },
      error: (err: unknown) => {
        logger.error('Itinerary subscription error', err);
      },
    });


    await new Promise(r => setTimeout(r, 500));

    try {
      const res: any = await client.graphql({ query: CREATE_ITINERARY, variables: { input } });
      const { status } = res.data.createItinerary;
      logger.info('Itinerary created', { sessionId });

      if (status === 'VALIDATION_ERROR') {
        setError('Validation failed');
        setPhase('error');
      }
    } catch (err: any) {
      logger.error('Mutation error', err);
      setError(err?.errors?.[0]?.message || 'Failed to create itinerary');
      setPhase(AGENT_PHASES.ERROR);
    }
  }, [client, fetchItinerary]);

  const sendChat = useCallback(async (sessionId: string, message: string) => {
    setChatMessages(prev => [...prev, { role: 'user', text: message }]);
    setChatLoading(true);
    chatBufferRef.current = '';

    const idleTimer = { current: null as ReturnType<typeof setTimeout> | null };
    const resetIdle = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        setChatLoading(false);
      }, 8000);
    };

    // Reuse existing subscription if still connected for same session
    if (!chatSubRef.current) {
      chatSubRef.current = (client.graphql({
        query: ON_AGENT_UPDATE,
        variables: { sessionId },
      }) as any).subscribe({
        next: (msg: any) => {
          const raw = msg?.data?.onAgentUpdate;
          if (!raw) return;
          if (raw.eventType === 'CHAT_MESSAGE') {
            chatBufferRef.current += raw.message;
            setChatMessages(prev => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return [...prev.slice(0, -1), { role: 'assistant', text: chatBufferRef.current }];
              }
              return [...prev, { role: 'assistant', text: chatBufferRef.current }];
            });
            resetIdle();
          }
          if (raw.eventType === 'TOOL_START' || raw.eventType === 'TOOL_END') {
            resetIdle();
          }
          if (raw.eventType === AGENT_EVENT_TYPES.AGENT_COMPLETE || raw.eventType === AGENT_EVENT_TYPES.AGENT_ERROR) {
            if (idleTimer.current) clearTimeout(idleTimer.current);
            setChatLoading(false);
            if (raw.eventType === AGENT_EVENT_TYPES.AGENT_COMPLETE) {
              fetchItinerary(sessionId);
            }
          }
        },
        error: () => { if (idleTimer.current) clearTimeout(idleTimer.current); setChatLoading(false); },
      });
    }

    try {
      await client.graphql({
        query: SEND_CHAT,
        variables: { input: { sessionId, type: 'chat', message } },
      });
      resetIdle();
    } catch (err) {
      logger.error('Chat mutation error', err);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      setChatLoading(false);
    }
  }, [client, fetchItinerary]);

  const reset = useCallback(() => {
    subRef.current?.unsubscribe();
    subRef.current = null;
    itSubRef.current?.unsubscribe();
    itSubRef.current = null;
    chatSubRef.current?.unsubscribe();
    chatSubRef.current = null;
    setEvents([]);
    setPhase('idle');
    setItinerary(null);
    setError(null);
    setChatMessages([]);
    setChatLoading(false);
  }, []);

  return { events, phase, itinerary, error, generateItinerary, sendChat, chatMessages, chatLoading, reset };
}
